import { addMinutes } from 'date-fns';

/**
 * SHER-834 — how long a prescription lasts, and the single place that turns that into
 * `treatment-finish`.
 *
 * Two paths write a prescription row: SingleTreatmentSelector's manual sub-form and the
 * pharmacy picker. They used to answer "when does this end?" independently — the manual form
 * computed the date from the entered duration, the picker wrote null unconditionally — and that
 * divergence IS the bug: a null end date means "chronique / à long terme" on read
 * (computePrescriptionDuration), so the picker asserted a long-term treatment nobody had
 * declared. One function now owns the answer, so the two paths cannot disagree again.
 */

export const DAYS = 'days';
export const WEEKS = 'weeks';
export const MONTHS = 'months';

/** Minutes per unit, matching what the manual sub-form has always used. */
const UNIT_MINUTES: Record<string, number> = {
    [DAYS]: 1440,
    [WEEKS]: 10080,
    [MONTHS]: 43200,
};

/**
 * Either an explicit chronic treatment, or an amount + unit. There is deliberately no third
 * state that means "the clinician never said": a caller that has not asked cannot produce this
 * value, which is what keeps the unasked case out of the persisted row.
 */
export interface PrescriptionDuration {
    chronic: boolean;
    amount?: number | null;
    unit?: string | null;
}

/**
 * Whether a duration has actually been chosen. Used to gate a confirm, exactly as the manual
 * sub-form refuses to save while neither the duration boxes nor the "chronique" checkbox are
 * filled in.
 */
export function isDurationChosen(duration?: PrescriptionDuration | null): boolean {
    if (!duration) {
        return false;
    }
    return duration.chronic || (duration.amount != null && !!duration.unit);
}

/**
 * The row's `treatment-finish`.
 *
 * Null for a chronic treatment — that is what a missing end date has always meant on read, and
 * 71% of CHOM's own prescription rows (2.067 of 2.898, measured 2026-09-03) are stored that
 * way. Null is therefore a legitimate value; what is not legitimate is reaching it without the
 * clinician saying so, which `isDurationChosen` is there to prevent.
 */
export function prescriptionFinishDate(start: Date, duration?: PrescriptionDuration | null): Date | null {
    if (!duration || duration.chronic) {
        return null;
    }
    const unitMinutes = duration.unit ? UNIT_MINUTES[duration.unit] : undefined;
    if (duration.amount == null || !unitMinutes) {
        return null;
    }
    return addMinutes(start, duration.amount * unitMinutes);
}
